import type { TopicIndexData, TopicEntry } from "@/lib/topics.server";

interface Def {
  h1: string;
  unit: string;
  metricLabel: string;
}

const n = (v: number) => v.toLocaleString("en-US");

/**
 * Analysis paragraphs for a topic chart, generated from that chart's own rows:
 * the range between first and last place, how tightly the field clusters,
 * which brands dominate and how the record is spread over model years.
 */
export function topicNarrative(def: Def, data: TopicIndexData): string[] {
  const e: TopicEntry[] = data.entries;
  if (e.length < 5) return [];
  const metric = def.metricLabel.toLowerCase();
  const top = e[0]!;
  const last = e[e.length - 1]!;
  const values = e.map((x) => x.value).sort((a, b) => a - b);
  const median = values[Math.floor(values.length / 2)]!;
  const q1 = values[Math.floor(values.length * 0.25)]!;
  const q3 = values[Math.floor(values.length * 0.75)]!;
  const out: string[] = [];

  out.push(
    `This chart ranks ${n(e.length)} models by ${metric}, from the ${top.year} ${top.makeName} ${top.modelName} at ${n(top.value)} ${def.unit} down to the ${last.year} ${last.makeName} ${last.modelName} at ${n(last.value)} ${def.unit}. The median entry records ${n(median)} ${def.unit}, and the middle half of the field falls between ${n(q1)} and ${n(q3)} ${def.unit} — a useful reference point, because a figure inside that band is ordinary for the segment rather than remarkable.`,
  );

  const byMake = new Map<string, { count: number; best: TopicEntry }>();
  for (const x of e) {
    const cur = byMake.get(x.makeName);
    if (!cur) byMake.set(x.makeName, { count: 1, best: x });
    else {
      cur.count += 1;
      if (x.value > cur.best.value) cur.best = x;
    }
  }
  const ranked = [...byMake.entries()].sort((a, b) => b[1].best.value - a[1].best.value).slice(0, 3);
  if (ranked.length === 3) {
    out.push(
      `Measured on each brand's strongest entry, ${ranked[0]![0]} leads with the ${ranked[0]![1].best.modelName} at ${n(ranked[0]![1].best.value)} ${def.unit}, followed by ${ranked[1]![0]} (${ranked[1]![1].best.modelName}, ${n(ranked[1]![1].best.value)}) and ${ranked[2]![0]} (${ranked[2]![1].best.modelName}, ${n(ranked[2]![1].best.value)}). ${byMake.size} manufacturers appear in the ranking overall, so ${metric} at this level is not the property of any single badge.`,
    );
  }

  const byYear = new Map<number, number>();
  for (const x of e) byYear.set(x.year, (byYear.get(x.year) ?? 0) + 1);
  const yrs = [...byYear.keys()].sort((a, b) => a - b);
  out.push(
    `Each model appears once, represented by its best-performing configuration, with model years running from ${yrs[0]} to ${yrs[yrs.length - 1]} and ${n(byYear.get(data.latestYear) ?? 0)} entries drawn from the ${data.latestYear} model year. Figures come from EPA and NHTSA publications rather than manufacturer marketing, and a model missing from the list is one with no published measurement for this specification — not one that scored zero. Open any row for the full year-by-year record of that vehicle, including the configurations that did not top its own range.`,
  );

  return out;
}
